/**
 * Landing del workshop del Vault: qué es, cuánto dura, capítulos, y las
 * preferencias de reproducción (velocidad, subtítulos, cursor, voz, música).
 * Oscura a propósito, como una sala antes de que arranque el video.
 */
import { ArrowLeft, Captions, Keyboard, MousePointer2, Music2, Play, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { chapterSeconds, fmtSeconds } from './engine';
import type { Workshop, WorkshopManifest, WorkshopSettings, WorkshopSpeed } from './types';

interface WorkshopLandingProps {
  workshop: Workshop;
  manifest: WorkshopManifest;
  settings: WorkshopSettings;
  onSettings: (patch: Partial<WorkshopSettings>) => void;
  onPlay: (fromChapter: number) => void;
}

const SPEEDS: WorkshopSpeed[] = [1, 1.25, 1.5];

export function WorkshopLanding({ workshop, manifest, settings, onSettings, onPlay }: WorkshopLandingProps) {
  const { t } = useTranslation();
  const durations = workshop.chapters.map((c) => chapterSeconds(c, manifest));
  const total = durations.reduce((a, b) => a + b, 0);
  const minutes = Math.max(1, Math.round(total / 60));

  const toggles: Array<{ key: keyof Omit<WorkshopSettings, 'speed'>; icon: typeof Captions; label: string; on: string; off: string }> = [
    { key: 'subtitles', icon: Captions, label: t('vault.workshop.subtitles', 'Subtítulos'), on: t('vault.workshop.subtitles_on', 'Activados'), off: t('vault.workshop.subtitles_off', 'Desactivados') },
    { key: 'cursor', icon: MousePointer2, label: t('vault.workshop.cursor', 'Cursor guía'), on: t('vault.workshop.cursor_on', 'Visible'), off: t('vault.workshop.cursor_off', 'Oculto') },
    { key: 'voice', icon: Volume2, label: t('vault.workshop.voice', 'Voz en off'), on: t('vault.workshop.voice_on', 'Activada'), off: t('vault.workshop.voice_off', 'Silenciada') },
    { key: 'music', icon: Music2, label: t('vault.workshop.music', 'Música de fondo'), on: t('vault.workshop.music_on', 'Activada'), off: t('vault.workshop.music_off', 'Apagada') },
  ];

  return (
    <div className="relative -m-3 min-h-[calc(100vh-4rem)] overflow-hidden bg-[#07100f] text-white sm:-m-4 md:-m-6">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand-green/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-52 right-0 h-[480px] w-[480px] rounded-full bg-brand-green/8 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-5 py-6 md:px-8 md:py-8">
        <div className="flex items-center justify-between text-sm">
          <span className="font-display text-lg">{t('vault.workshop.brand', 'Winclap · Internal')}</span>
          <Link to="/vault" className="inline-flex items-center gap-1.5 text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            {t('vault.workshop.back', 'Volver al Vault')}
          </Link>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-green">{t('vault.workshop.eyebrow', 'Workshop · Vault de Winclap')}</div>
            <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl" style={{ textWrap: 'balance' }}>
              {t('vault.workshop.title', 'Del sheet de contraseñas al Vault, en {{m}} minutos.', { m: minutes })}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/70">
              {t(
                'vault.workshop.body',
                'Un recorrido guiado por el Vault real, con datos de ejemplo: cómo se entra, cómo se revela una contraseña, el código MFA compartido, accesos, rotación, links temporales y auditoría. La pantalla se opera sola; vos mirás.'
              )}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => onPlay(0)}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-green px-5 py-3 text-base font-semibold text-[#06110f] shadow-[0_10px_30px_rgba(28,201,185,0.35)] transition-colors hover:bg-[#3fd8ca]"
              >
                <Play className="h-5 w-5 fill-current" />
                {t('vault.workshop.play', 'Reproducir workshop')}
              </button>
              <span className="text-sm text-white/60">
                {t('vault.workshop.meta', '{{n}} capítulos · {{d}}', { n: workshop.chapters.length, d: fmtSeconds(total) })}
              </span>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/4 p-4">
                <div className="text-xs text-white/60">{t('vault.workshop.speed', 'Velocidad')}</div>
                <div className="mt-2 inline-flex rounded-lg bg-black/40 p-1">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => onSettings({ speed: s })}
                      className={cn('rounded-md px-2.5 py-1 text-sm transition-colors', settings.speed === s ? 'bg-brand-green font-semibold text-[#06110f]' : 'text-white/70 hover:text-white')}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </div>
              {toggles.map(({ key, icon: Icon, label, on, off }) => (
                <label key={key} className="flex cursor-pointer items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/4 p-4 transition-colors hover:border-brand-green/50">
                  <div className="flex items-start gap-2.5">
                    <Icon className="mt-0.5 h-4 w-4 text-brand-green" />
                    <div>
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs text-white/55">{settings[key] ? on : off}</div>
                    </div>
                  </div>
                  <Switch checked={settings[key]} onCheckedChange={(v) => onSettings({ [key]: v })} className="data-[state=checked]:bg-brand-green" />
                </label>
              ))}
            </div>

            <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-white/50">
              <Keyboard className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {t('vault.workshop.keys_hint', 'Durante la reproducción: Espacio pausa · → siguiente capítulo · Esc sale. Los controles aparecen al mover el mouse; para grabar, quedate quieto y desaparecen solos. Todo lo que ves son datos de demo.')}
              </span>
            </p>
          </div>

          <ol className="self-start rounded-3xl border border-white/10 bg-white/3 p-2">
            {workshop.chapters.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPlay(i)}
                  title={t('vault.workshop.chapter_from', 'Empezar desde este capítulo')}
                  className="group flex w-full items-center gap-4 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-white/6"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-green/15 text-sm font-semibold text-brand-green transition-colors group-hover:bg-brand-green group-hover:text-[#06110f]">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{c.title}</span>
                    <span className="block truncate text-sm text-white/55">{c.subtitle}</span>
                  </span>
                  <span className="shrink-0 font-mono text-xs text-white/45">{fmtSeconds(durations[i])}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
